/**
 * Compact markdown renderer for visual labels.
 */
'use client';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import type { Marked, Token } from 'marked';
import { Cmarkdownparagraph } from '../Cmarkdownparagraph/Cmarkdownparagraph';
import { MarkdownTokenType } from '../Cmarkdown/markdown-token-type';
import { renderInlineContent, renderInlineTokens, SHARED_MARKDOWN_PARSER, } from '../Cmarkdown/markdown-inline-renderer';
import { startsWithHebrew } from '../Cmarkdown/markdown-hebrew';
import { getCmarkdownTextColor } from './Cmarkdowntext-color';
import type { CmarkdowntextProps } from './Cmarkdowntext-types';
const COMPACT_MARKDOWN_PARAGRAPH_STYLE: CSSProperties = {
    marginBottom: 0,
};
const COMPACT_MARKDOWN_WRAPPER_STYLE: CSSProperties = {
    display: 'inline-flex',
    width: 'auto',
    minWidth: 0,
};
interface RenderCompactTokensInput {
    tokens: Token[];
    markdownParser: Marked;
    textSize?: number;
    textWeight?: CmarkdowntextProps['weight'];
    textAlign?: CmarkdowntextProps['textAlign'];
    paragraphStyle?: CSSProperties;
    paragraphTextStyle?: CSSProperties;
}
function renderCompactTokens(input: RenderCompactTokensInput): ReactNode[] {
    return input.tokens.map((token, index) => {
        const key = `${token.type}-${index}`;
        if (token.type === MarkdownTokenType.Space) {
            return null;
        }
        const tokenText = 'text' in token ? token.text : '';
        const tokenChildren = 'tokens' in token ? token.tokens : undefined;
        const children = tokenChildren && tokenChildren.length > 0
            ? renderInlineTokens({
                tokens: tokenChildren,
                markdownParser: input.markdownParser,
                evidenceEnabled: false,
            })
            : renderInlineContent({
                text: tokenText,
                markdownParser: input.markdownParser,
                evidenceEnabled: false,
            });
        return (<Cmarkdownparagraph key={key} textSize={input.textSize} textWeight={input.textWeight} textAlign={input.textAlign} style={{
                ...COMPACT_MARKDOWN_PARAGRAPH_STYLE,
                ...input.paragraphStyle,
            }} textStyle={input.paragraphTextStyle} hebrew={startsWithHebrew(tokenText)}>
        {children}
      </Cmarkdownparagraph>);
    });
}
export function CmarkdownLabelText({ content, size, weight, color, opacity, textAlign, className, style, paragraphStyle, textStyle, }: CmarkdowntextProps): ReactElement {
    const resolvedTextColor = getCmarkdownTextColor({ color, opacity });
    const renderedContent = useMemo(() => {
        if (!content || content.trim() === '') {
            return null;
        }
        try {
            const tokens = SHARED_MARKDOWN_PARSER.lexer(content);
            return renderCompactTokens({
                tokens,
                markdownParser: SHARED_MARKDOWN_PARSER,
                textSize: size,
                textWeight: weight,
                textAlign,
                paragraphStyle,
                paragraphTextStyle: {
                    color: resolvedTextColor,
                    ...textStyle,
                },
            });
        }
        catch {
            return (<Cmarkdownparagraph textSize={size} textWeight={weight} textAlign={textAlign} style={{
                    ...COMPACT_MARKDOWN_PARAGRAPH_STYLE,
                    ...paragraphStyle,
                }} textStyle={{
                    color: resolvedTextColor,
                    ...textStyle,
                }} hebrew={startsWithHebrew(content)}>
          {content}
        </Cmarkdownparagraph>);
        }
    }, [
        content,
        paragraphStyle,
        resolvedTextColor,
        size,
        textAlign,
        textStyle,
        weight,
    ]);
    return (
      <div className={className} style={{
            ...COMPACT_MARKDOWN_WRAPPER_STYLE,
            ...style,
        }}>
        {renderedContent}
      </div>
    );
}
